import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { adminLocalizationApi, adminLocalizationKeys } from '@/api/admin/localization';
import { Progress } from '@/components';
import { Button as CustomButton } from '@/components';
import { Col, Container, Flex } from '@/ui/layout';
import { SHOWED_SECTIONS_COUNT } from '@/utils/static/kyc';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Typography, Select, Button } from 'antd';
import { Title } from '@/ui/typography';
import { queryClient } from '@/app/QueryClientProvider';
import { sortWithNulls } from '@/utils/helpers';
import { useCountries, usePopups } from '@/hooks';
import arrowBackIcon from '@/assets/icons/arrow_back.svg';
import { QuestionnaireSidePanel } from '@/components';

export const TranslateQuestionnaire = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { showErrorToast, showSuccessToast } = usePopups();
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const { languages } = useCountries();

  const { data: allQuestionnaires } = useQuery({
    queryKey: [adminLocalizationKeys.getAllQuestionnaires],
    queryFn: adminLocalizationApi.getAllQuestionnaires,
    select: (data1) => data1.data
  });
  const { data: allQuestionnairesTranslated } = useQuery({
    queryKey: [adminLocalizationKeys.getAllQuestionnairesTranslation, selectedLanguage],
    queryFn: () => adminLocalizationApi.getAllQuestionnairesTranslation(selectedLanguage),
    select: (data1) => data1.data
  });
  const { data, isLoading } = useQuery({
    queryKey: [adminLocalizationKeys.getQuestionnaireSections],
    queryFn: () => adminLocalizationApi.getQuestionnaireSections({ questionSectionTypeId: id }),
    select: (data1) => data1.data
  });
  const { data: questionnaireTranslation } = useQuery({
    queryKey: [adminLocalizationKeys.getQuestionnaireSectionsTranslation, selectedLanguage],
    queryFn: () =>
      adminLocalizationApi.getQuestionnaireSectionsTranslation({
        questionSectionTypeId: id,
        languageId: selectedLanguage
      }),
    select: (data1) => data1.data
  });
  const { data: isActive } = useQuery({
    queryKey: [adminLocalizationKeys.isQuestTranslationActive, selectedLanguage],
    queryFn: () =>
      adminLocalizationApi.isQuestTranslationActive({
        questionSectionTypeId: id,
        languageId: selectedLanguage
      }),
    select: (data1) => data1.data
  });

  const [currentSection, setCurrentSection] = useState(0);

  const currentQuest = allQuestionnaires?.find((quest) => quest.id === id);
  const currentQuestTranslated = allQuestionnairesTranslated?.find((quest) => quest.id === id);

  const saveQuestTranslation = async (value, prevValue) => {
    if (value !== prevValue) {
      try {
        await adminLocalizationApi.setQuestionnaireInfo({
          questionSectionTypeId: id,
          languageId: selectedLanguage,
          name: value
        });

        queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getAllQuestionnairesTranslation]
        });
        showSuccessToast('Saved');
      } catch (e) {
        console.error(e);
        showErrorToast(e, true);
      }
    }
  };

  const saveTranslation = async ({
    questionSectionId,
    questionGroupId,
    questionId,
    availableAnswerId,
    value,
    prevValue
  }) => {
    if (value !== prevValue || !prevValue) {
      try {
        await adminLocalizationApi.setQuestionnaireSection({
          questionSectionTypeId: id,
          languageId: selectedLanguage,
          questionSectionId,
          questionGroupId,
          questionId,
          availableAnswerId,
          newText: value
        });

        queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSectionsTranslation]
        });
        showSuccessToast('Saved');
      } catch (e) {
        console.error(e);
        showErrorToast(e, true);
      }
    }
  };

  const enableTranslation = async () => {
    try {
      await adminLocalizationApi.activateQuestionnaireTranslation({
        questionSectionTypeId: id,
        languageId: selectedLanguage
      });

      queryClient.refetchQueries({ queryKey: [adminLocalizationKeys.isQuestTranslationActive] });
      showSuccessToast('Translation enabled');
    } catch (e) {
      showErrorToast(e, true);
    }
  };

  if (!data) return t('Loading') + '...';

  if (isLoading) return t('Loading') + '...';

  return (
    <Flex gap={20} wrap>
      <Flex $marginTop={80}>
        <QuestionnaireSidePanel
          step={currentSection + 1}
          setStep={setCurrentSection}
          steps={data}
          width={250}
        />
      </Flex>

      <Col>
        <MainContainer $paddingY={64} $paddingRight={23} $paddingBottom={'0'} $paddingLeft={15}>
          <div>
            <Flex
              $margin={-1.5 * 8}
              justify={'flex-end'}
              $marginTop={-48}
              $marginBottom={24}
              align={'center'}
            >
              <Col>
                <Container $width={100}>
                  <CustomButton
                    type='text'
                    size='large'
                    text='Back'
                    icon={<img src={arrowBackIcon} alt='arrow-back' />}
                    onClick={() => navigate('/localization')}
                  />
                </Container>
              </Col>
              <Col $auto>
                <Flex gap={24}>
                  {isActive && (
                    <Button disabled={isActive.active} type='primary' onClick={enableTranslation}>
                      {!isActive.active ? t('Enable translation') : t('Enabled')}
                    </Button>
                  )}

                  {languages && (
                    <Select
                      style={{ width: 180 }}
                      onChange={(value) => setSelectedLanguage(value)}
                      options={languages.map((item) => ({ value: item.id, label: item.language }))}
                      placeholder={t('Select language')}
                    />
                  )}
                </Flex>
              </Col>
            </Flex>

            <Progress isAdmin={true} steps={data} currentStep={currentSection + 1} />

            <Flex gap={16} $marginBottom={24}>
              {currentQuest && (
                <Title level={4} $marginTop={0} $marginBottom={0}>
                  {currentQuest.name}
                </Title>
              )}
              {currentQuestTranslated && (
                <Title
                  editable={
                    selectedLanguage
                      ? {
                          onChange: (value) =>
                            saveQuestTranslation(value, currentQuestTranslated.name)
                        }
                      : false
                  }
                  level={4}
                  disabled={!selectedLanguage}
                  $marginTop={0}
                  $marginBottom={0}
                >
                  {currentQuestTranslated.name}
                </Title>
              )}
            </Flex>

            <Flex gap={16}>
              <RenderQuestionnaire
                questionnaire={data}
                currentSection={currentSection}
                selectedLanguage={selectedLanguage}
                saveTranslation={saveTranslation}
              />
              <RenderQuestionnaire
                questionnaire={questionnaireTranslation}
                currentSection={currentSection}
                selectedLanguage={selectedLanguage}
                saveTranslation={saveTranslation}
                editable
              />
            </Flex>
          </div>

          <StickyBox $paddingY={16} $paddingX={16}>
            <Flex gap={24} justify='space-between'>
              <Container $width={124}>
                <Button
                  size='large'
                  onClick={() => setCurrentSection(currentSection - 1)}
                  disabled={currentSection === 0}
                >
                  {t('Back')}
                </Button>
              </Container>

              <Container $width={124}>
                <Button
                  type='primary'
                  size='large'
                  onClick={() => setCurrentSection(currentSection + 1)}
                  disabled={currentSection + 1 >= data.length}
                >
                  {t('Next')}
                </Button>
              </Container>
            </Flex>
          </StickyBox>
        </MainContainer>
      </Col>
    </Flex>
  );
};

const RenderQuestionnaire = ({
  questionnaire,
  currentSection,
  selectedLanguage,
  saveTranslation,
  editable = false
}) => {
  return (
    <div>
      {questionnaire &&
        questionnaire
          .slice(currentSection, currentSection + SHOWED_SECTIONS_COUNT)
          .map((sectionObj, index) => (
            <div key={index}>
              <Typography.Title
                disabled={editable && !selectedLanguage}
                editable={
                  editable && selectedLanguage
                    ? {
                        onChange: (value) =>
                          saveTranslation({
                            questionSectionId: sectionObj.id,
                            value: value,
                            prevValue: sectionObj.section
                          })
                      }
                    : false
                }
                level={4}
              >
                {sectionObj.section}
              </Typography.Title>

              <Container $paddingLeft={16}>
                {sectionObj.questionGroups
                  ?.sort((a, b) => sortWithNulls(Number(a.sortOrder), Number(b.sortOrder)))
                  .map((questionGroup, index) => (
                    <Container key={index} $paddingLeft={8}>
                      {questionGroup.title && (
                        <Typography.Title
                          disabled={editable && !selectedLanguage}
                          editable={
                            editable && selectedLanguage
                              ? {
                                  onChange: (value) =>
                                    saveTranslation({
                                      questionGroupId: questionGroup.id,
                                      value: value,
                                      prevValue: questionGroup.title
                                    })
                                }
                              : false
                          }
                          level={4}
                        >
                          {questionGroup.title}
                        </Typography.Title>
                      )}

                      {questionGroup.questions
                        .sort((a, b) => sortWithNulls(Number(a.sortOrder), Number(b.sortOrder)))
                        .map((question, index) => (
                          <Container key={index} $paddingLeft={16}>
                            {question.question && (
                              <Typography.Title
                                disabled={editable && !selectedLanguage}
                                editable={
                                  editable && selectedLanguage
                                    ? {
                                        onChange: (value) =>
                                          saveTranslation({
                                            questionId: question.id,
                                            value: value,
                                            prevValue: question.question
                                          })
                                      }
                                    : false
                                }
                                level={5}
                              >
                                {question.question}
                              </Typography.Title>
                            )}

                            {question.availableAnswers
                              .sort((a, b) =>
                                sortWithNulls(Number(a.sortOrder), Number(b.sortOrder))
                              )
                              .map((answer, index) => (
                                <Container key={index} $paddingLeft={16}>
                                  <Typography.Text
                                    disabled={editable && !selectedLanguage}
                                    editable={
                                      editable && selectedLanguage
                                        ? {
                                            onChange: (value) =>
                                              saveTranslation({
                                                availableAnswerId: answer.id,
                                                value: value,
                                                prevValue: answer.answer
                                              })
                                          }
                                        : false
                                    }
                                  >
                                    {answer.answer}
                                  </Typography.Text>
                                </Container>
                              ))}
                          </Container>
                        ))}
                    </Container>
                  ))}
              </Container>
            </div>
          ))}
    </div>
  );
};

const MainContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;
`;
const StickyBox = styled(Container)`
  background: #ffffff;
  box-shadow: 0px -1px 3px rgba(0, 0, 0, 0.15);
  position: sticky;
  bottom: 0;
  margin: 40px -23px 0 -15px;
`;
